import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Loader2, CheckCircle2, Edit3, X } from 'lucide-react';
import { api } from '../../lib/api';
import ImageInput from '../../components/admin/ImageInput';
import { Field, TextInput, TextArea, NumberInput, Select, Toggle, Button } from '../../components/admin/FormControls';

const CATEGORIES = [
  { value: 'presidential', label: 'Presidential' },
  { value: 'government', label: 'Government' },
  { value: 'education', label: 'Education' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'investment', label: 'Investment' },
  { value: 'health', label: 'Health' },
  { value: 'security', label: 'Security' }
];

const empty = {
  title: '',
  slug: '',
  category: 'government',
  date: '',
  image_url: '',
  excerpt: '',
  body: '',
  author: 'Office of Communications',
  order: 0,
  published: true
};

const AdminNews = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/news');
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(''), 2200);
  };

  const startNew = () =>
    setEditing({ ...empty, order: (items[items.length - 1]?.order || 0) + 1 });

  const startEdit = (n) => setEditing({ ...n });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        const { id, ...rest } = editing;
        await api.put(`/news/${id}`, rest);
      } else {
        await api.post('/news', editing);
      }
      flash('Saved');
      setEditing(null);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete news "${n.title}"?`)) return;
    await api.delete(`/news/${n.id}`);
    flash('Deleted');
    load();
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#0B2545]">News</h1>
          <p className="mt-1 text-[13.5px] text-[#0B2545]/65">
            Manage news articles displayed on the home page and news list.
          </p>
        </div>
        <Button variant="gold" onClick={startNew}>
          <Plus size={14} /> Add news
        </Button>
      </div>

      {msg && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}

      {loading ? (
        <div className="text-[#0B2545]/60"><Loader2 className="inline animate-spin" size={14} /> Loading...</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-[#E6DDC8] bg-white">
          <table className="w-full text-left text-[13.5px]">
            <thead className="bg-[#FAF6EC] text-[12px] uppercase tracking-wide text-[#0B2545]/65">
              <tr>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6DDC8]">
              {items.map((n) => (
                <tr key={n.id} className="text-[#0B2545]">
                  <td className="px-4 py-3">{n.order}</td>
                  <td className="px-4 py-3 font-medium">{n.title}</td>
                  <td className="px-4 py-3 capitalize text-[#C9A86A]">{n.category}</td>
                  <td className="px-4 py-3 text-[#0B2545]/70">{n.date}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      n.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {n.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" onClick={() => startEdit(n)}>
                      <Edit3 size={13} /> Edit
                    </Button>
                    <button
                      onClick={() => remove(n)}
                      className="ml-2 text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#0B2545]/55">No news yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between border-b border-[#E6DDC8] bg-white px-6 py-4">
              <h3 className="font-serif text-lg font-bold text-[#0B2545]">
                {editing.id ? 'Edit news' : 'New news article'}
              </h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-4 p-6">
              <ImageInput
                label="Cover image"
                value={editing.image_url}
                onChange={(v) => setEditing({ ...editing, image_url: v })}
                height={200}
              />
              <Field label="Title">
                <TextInput value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Category">
                  <Select
                    value={editing.category}
                    onChange={(v) => setEditing({ ...editing, category: v })}
                    options={CATEGORIES}
                  />
                </Field>
                <Field label="Date" hint="e.g. 18 May 2026">
                  <TextInput value={editing.date} onChange={(v) => setEditing({ ...editing, date: v })} />
                </Field>
                <Field label="Author">
                  <TextInput value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
                </Field>
              </div>
              <Field label="Excerpt (short summary)">
                <TextArea rows={2} value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} />
              </Field>
              <Field label="Body (full article)">
                <TextArea rows={8} value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} />
              </Field>
              <div className="flex items-end gap-4">
                <Field label="Order">
                  <NumberInput value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} />
                </Field>
                <div className="pb-2">
                  <Toggle
                    checked={editing.published}
                    onChange={(v) => setEditing({ ...editing, published: v })}
                    label={editing.published ? 'Published' : 'Draft'}
                  />
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-[#E6DDC8] bg-white px-6 py-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
